import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { ledgerAPI, propertiesAPI } from '../../lib/api';
import { getCurrencySymbol } from '../../lib/currencies';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { format } from 'date-fns';
import {
  DollarSign, Loader2, Plus, X, Trash2, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Clock, Receipt, CreditCard, Banknote,
  Search, CalendarDays, Building2, Users, ArrowDownLeft, ArrowUpRight,
  Pencil, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const METHODS = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: DollarSign },
  { value: 'qr', label: 'QR', icon: Receipt },
  { value: 'pos', label: 'POS', icon: CreditCard },
  { value: 'online', label: 'Online', icon: DollarSign },
];

function PaymentStatusBadge({ status }) {
  if (status === 'paid') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="status-paid"><CheckCircle className="h-3 w-3 mr-1" />Paid</Badge>;
  if (status === 'partial') return <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="status-partial"><Clock className="h-3 w-3 mr-1" />Partial</Badge>;
  return <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="status-pending"><AlertCircle className="h-3 w-3 mr-1" />Unpaid</Badge>;
}

/* ════════════ Record Payment Modal ════════════ */
function RecordPaymentModal({ booking, accounts, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [accountId, setAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const currency = booking?.currency || 'USD';
  const sym = getCurrencySymbol(currency);
  const balance = (booking?.total_amount || 0) - (booking?.total_paid || booking?.amount_paid || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      await ledgerAPI.recordPayment({
        booking_id: booking.id, amount: parseFloat(amount), currency, method,
        note: note || null, payment_account_id: accountId || null,
      });
      toast.success('Payment recorded');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="record-payment-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-semibold">Record Payment</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium">{booking.guest_name} — {booking.booking_reference}</p>
            <p className="text-muted-foreground">{booking.room_type_name} · {booking.property_name}</p>
            <div className="flex justify-between pt-1">
              <span>Total: {sym}{(booking.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              <span className="font-semibold text-red-600">Balance: {sym}{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Amount ({currency})</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{sym}</span>
              <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="pl-8" required data-testid="payment-amount-input" />
            </div>
            <button type="button" onClick={() => setAmount(balance.toFixed(2))} className="text-xs text-primary hover:underline">Fill full balance ({sym}{balance.toFixed(2)})</button>
          </div>

          <div className="space-y-2">
            <Label>Payment Account</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="payment-account-select"><SelectValue placeholder="Select account (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No account —</SelectItem>
                {(accounts || []).map(a => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.account_type === 'receivable' ? '↓' : '↑'} {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Method</Label>
            <div className="grid grid-cols-3 gap-2">
              {METHODS.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.value} type="button" onClick={() => setMethod(m.value)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${method === m.value ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border hover:border-primary/30'}`}
                    data-testid={`method-${m.value}`}
                  ><Icon className="h-3.5 w-3.5" />{m.label}</button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Payment reference, receipt #, etc." data-testid="payment-note-input" />
          </div>

          <Button type="submit" className="w-full" disabled={loading} data-testid="submit-payment-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Record Payment
          </Button>
        </form>
      </div>
    </div>
  );
}

/* ════════════ Booking Ledger Detail ════════════ */
function BookingLedgerDetail({ bookingId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try { const res = await ledgerAPI.getBookingLedger(bookingId); setData(res.data); }
    catch { toast.error('Failed to load booking ledger'); }
    finally { setLoading(false); }
  }, [bookingId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleVoid = async (paymentId) => {
    if (!window.confirm('Void this payment?')) return;
    try { await ledgerAPI.voidPayment(paymentId); toast.success('Payment voided'); fetchData(); }
    catch { toast.error('Failed to void payment'); }
  };

  if (loading) return <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return null;
  const { booking, payments, total_amount, total_paid, balance, currency } = data;
  const sym = getCurrencySymbol(currency);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="booking-ledger-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div><h3 className="text-lg font-semibold">{booking.booking_reference}</h3><p className="text-sm text-muted-foreground">{booking.guest_name} · {booking.room_type_name}</p></div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/50 rounded-lg p-3 text-center"><p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p><p className="text-lg font-semibold">{sym}{total_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></div>
            <div className="bg-emerald-50 rounded-lg p-3 text-center"><p className="text-xs text-emerald-600 uppercase tracking-wider">Paid</p><p className="text-lg font-semibold text-emerald-700">{sym}{total_paid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></div>
            <div className={`rounded-lg p-3 text-center ${balance > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}><p className={`text-xs uppercase tracking-wider ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Balance</p><p className={`text-lg font-semibold ${balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{sym}{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></div>
          </div>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold mb-3">Payment History</h4>
            {payments.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No payments recorded</p> : (
              <div className="space-y-2">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg text-sm" data-testid={`payment-row-${p.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{sym}{p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        <Badge variant="outline" className="text-[10px] capitalize">{p.method}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.created_at ? format(new Date(p.created_at), 'd MMM yyyy, HH:mm') : '—'}{p.recorded_by_name && ` · ${p.recorded_by_name}`}{p.note && <span className="text-gray-400 ml-1.5">— {p.note}</span>}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleVoid(p.id)} data-testid={`void-payment-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════ Account Form Modal ════════════ */
function AccountFormModal({ account, properties, onClose, onSuccess }) {
  const [form, setForm] = useState({
    name: account?.name || '', account_type: account?.account_type || 'receivable',
    contact_name: account?.contact_name || '', contact_email: account?.contact_email || '',
    contact_phone: account?.contact_phone || '', property_id: account?.property_id || '',
    notes: account?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Account name is required'); return; }
    setSaving(true);
    try {
      if (account) { await ledgerAPI.updateAccount(account.id, form); toast.success('Account updated'); }
      else { await ledgerAPI.createAccount(form); toast.success('Account created'); }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save account'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle data-testid="account-modal-title">{account ? 'Edit Account' : 'New Payment Account'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label>Account Name *</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Booking.com, Travel Corp Ltd" required data-testid="account-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Type *</Label>
            <Select value={form.account_type} onValueChange={v => setForm(p => ({ ...p, account_type: v }))}>
              <SelectTrigger data-testid="account-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="receivable">Receivable (owed to you)</SelectItem>
                <SelectItem value="payable">Payable (you owe)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} data-testid="account-contact-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} data-testid="account-contact-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.contact_phone} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} data-testid="account-contact-phone" /></div>
            <div className="space-y-1.5">
              <Label>Property</Label>
              <Select value={form.property_id || 'all'} onValueChange={v => setForm(p => ({ ...p, property_id: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="account-property"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {(properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="account-notes" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="account-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{account ? 'Update' : 'Create'} Account</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════ Account Ledger Modal ════════════ */
function AccountLedgerModal({ accountId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ledgerAPI.getAccountLedger(accountId).then(r => setData(r.data)).catch(() => toast.error('Failed to load account ledger')).finally(() => setLoading(false));
  }, [accountId]);

  if (loading) return <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return null;
  const { account, transactions, total } = data;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="account-ledger-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{account.name}</h3>
              <Badge className={account.account_type === 'receivable' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>{account.account_type === 'receivable' ? 'Receivable' : 'Payable'}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{account.contact_name && `${account.contact_name} · `}{account.contact_email || ''}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-3 bg-muted/30 border-b flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{transactions.length} transactions</span>
          <span className="text-lg font-bold">{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="overflow-y-auto flex-1 p-5">
          {transactions.length === 0 ? <p className="text-center text-muted-foreground py-8">No transactions yet</p> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider border-b">
                  <th className="pb-2 pr-3">Date</th><th className="pb-2 pr-3">Reference</th><th className="pb-2 pr-3">Guest</th>
                  <th className="pb-2 pr-3">Method</th><th className="pb-2 pr-3 text-right">Amount</th><th className="pb-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`acct-txn-${t.id}`}>
                    <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{t.created_at ? format(new Date(t.created_at), 'd MMM yy') : '—'}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs">{t.booking_reference}</td>
                    <td className="py-2.5 pr-3">{t.guest_name}</td>
                    <td className="py-2.5 pr-3 capitalize">{t.method}</td>
                    <td className="py-2.5 pr-3 text-right font-medium">{t.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="py-2.5 text-right font-semibold">{t.running_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════ MAIN PAGE ════════════ */
export default function Ledger() {
  const [activeTab, setActiveTab] = useState('outstanding');
  const [outstanding, setOutstanding] = useState([]);
  const [payments, setPayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paymentModal, setPaymentModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [accountFormModal, setAccountFormModal] = useState(null); // null | {} | account
  const [accountLedgerModal, setAccountLedgerModal] = useState(null);
  const [totalPayments, setTotalPayments] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchOutstanding = useCallback(async () => {
    try {
      const params = propertyFilter ? { property_id: propertyFilter } : {};
      const res = await ledgerAPI.getOutstanding(params);
      setOutstanding(res.data.bookings || []);
    } catch { toast.error('Failed to load outstanding bookings'); }
  }, [propertyFilter]);

  const fetchPayments = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (propertyFilter) params.property_id = propertyFilter;
      const res = await ledgerAPI.listPayments(params);
      setPayments(res.data.payments || []);
      setTotalPayments(res.data.total || 0);
    } catch { toast.error('Failed to load payments'); }
  }, [propertyFilter]);

  const fetchAccounts = useCallback(async () => {
    try {
      const params = {};
      if (propertyFilter) params.property_id = propertyFilter;
      const res = await ledgerAPI.listAccounts(params);
      setAccounts(res.data.accounts || []);
    } catch { toast.error('Failed to load accounts'); }
  }, [propertyFilter]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [propsRes] = await Promise.all([propertiesAPI.list()]);
      setProperties(propsRes.data || []);
      await Promise.all([fetchOutstanding(), fetchPayments(), fetchAccounts()]);
    } catch { /* individual errors handled */ }
    finally { setLoading(false); }
  }, [fetchOutstanding, fetchPayments, fetchAccounts]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handlePaymentSuccess = () => { setPaymentModal(null); fetchOutstanding(); fetchPayments(); fetchAccounts(); };
  const handleAccountSaved = () => { setAccountFormModal(null); fetchAccounts(); };
  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this payment account?')) return;
    try { await ledgerAPI.deleteAccount(id); toast.success('Account deleted'); fetchAccounts(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const toggleRow = (id) => setExpandedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const filteredOutstanding = outstanding.filter(b => {
    if (searchQuery) { const q = searchQuery.toLowerCase(); if (!(b.guest_name || '').toLowerCase().includes(q) && !(b.booking_reference || '').toLowerCase().includes(q) && !(b.room_type_name || '').toLowerCase().includes(q)) return false; }
    if (dateFrom && b.check_in < dateFrom) return false;
    if (dateTo && b.check_in > dateTo) return false;
    return true;
  });

  const filteredPayments = payments.filter(p => {
    if (searchQuery) { const q = searchQuery.toLowerCase(); if (!(p.guest_name || '').toLowerCase().includes(q) && !(p.booking_reference || '').toLowerCase().includes(q)) return false; }
    if (dateFrom && p.created_at) { if (p.created_at.slice(0, 10) < dateFrom) return false; }
    if (dateTo && p.created_at) { if (p.created_at.slice(0, 10) > dateTo) return false; }
    return true;
  });

  const receivableAccounts = accounts.filter(a => a.account_type === 'receivable');
  const payableAccounts = accounts.filter(a => a.account_type === 'payable');
  const totalReceivable = receivableAccounts.reduce((s, a) => s + (a.total_amount || 0), 0);
  const totalPayable = payableAccounts.reduce((s, a) => s + (a.total_amount || 0), 0);
  const hasDateFilter = dateFrom || dateTo;
  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); };
  const totalOutstandingAmount = filteredOutstanding.reduce((sum, b) => sum + (b.balance || 0), 0);

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></AdminLayout>;

  const TABS = [
    { key: 'outstanding', label: 'Outstanding', count: filteredOutstanding.length, color: 'text-red-600' },
    { key: 'payments', label: 'All Payments', count: filteredPayments.length, color: '' },
    { key: 'receivable', label: 'Receivable', count: receivableAccounts.length, color: 'text-blue-600' },
    { key: 'payable', label: 'Payable', count: payableAccounts.length, color: 'text-orange-600' },
    { key: 'accounts', label: 'Accounts', count: accounts.length, color: '' },
  ];

  const renderAccountRow = (a) => (
    <Card key={a.id} className="border-border hover:shadow-sm transition-shadow" data-testid={`account-row-${a.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={`p-2.5 rounded-lg ${a.account_type === 'receivable' ? 'bg-blue-50' : 'bg-orange-50'}`}>
            {a.account_type === 'receivable' ? <ArrowDownLeft className="h-5 w-5 text-blue-600" /> : <ArrowUpRight className="h-5 w-5 text-orange-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{a.name}</span>
              <Badge variant="outline" className="text-[10px]">{a.account_type}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {a.contact_name && `${a.contact_name} · `}{a.contact_email || ''}{a.notes && <span className="text-gray-400 ml-1.5">— {a.notes}</span>}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-muted-foreground">{a.transaction_count || 0} transactions</p>
            <p className="text-lg font-bold">{(a.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setAccountLedgerModal(a.id)} title="View Ledger" data-testid={`view-acct-${a.id}`}><Eye className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setAccountFormModal(a)} title="Edit" data-testid={`edit-acct-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteAccount(a.id)} title="Delete" data-testid={`delete-acct-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="page-wrapper" data-testid="ledger-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="page-title">Payments</h1>
            <p className="text-sm text-muted-foreground mt-1">Track payments, manage accounts receivable and payable</p>
          </div>
          <Button onClick={() => setAccountFormModal({})} data-testid="add-account-btn"><Plus className="h-4 w-4 mr-1.5" /> New Account</Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-red-600">Outstanding</p>
              <p className="text-xl font-bold text-red-700 mt-1">{outstanding.length}</p>
              <p className="text-xs text-red-600/70 mt-0.5">{totalOutstandingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} balance due</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-emerald-600">Payments</p>
              <p className="text-xl font-bold text-emerald-700 mt-1">{totalPayments}</p>
              <p className="text-xs text-emerald-600/70 mt-0.5">total transactions</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-blue-600">Receivable</p>
              <p className="text-xl font-bold text-blue-700 mt-1">{totalReceivable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-xs text-blue-600/70 mt-0.5">{receivableAccounts.length} accounts</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-orange-600">Payable</p>
              <p className="text-xl font-bold text-orange-700 mt-1">{totalPayable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-xs text-orange-600/70 mt-0.5">{payableAccounts.length} accounts</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search guest, reference..." className="pl-9" data-testid="ledger-search" />
          </div>
          <select value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm appearance-none cursor-pointer" data-testid="ledger-property-filter">
            <option value="">All Properties</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={hasDateFilter ? 'default' : 'outline'} size="sm" className="gap-2 h-9" data-testid="ledger-date-range">
                <CalendarDays className="h-4 w-4" />{hasDateFilter ? `${dateFrom || '...'} — ${dateTo || '...'}` : 'Date Range'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="end">
              <div className="space-y-3">
                <p className="text-sm font-medium">Filter by date</p>
                <div className="flex items-center gap-2">
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">From</label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px]" data-testid="ledger-date-from" /></div>
                  <span className="text-muted-foreground mt-5">—</span>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">To</label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px]" data-testid="ledger-date-to" /></div>
                </div>
                {hasDateFilter && <Button variant="ghost" size="sm" onClick={clearDateFilter} className="text-xs w-full" data-testid="ledger-clear-date"><X className="h-3 w-3 mr-1" /> Clear dates</Button>}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b overflow-x-auto" data-testid="ledger-tabs">
          {TABS.map(t => (
            <button key={t.key} className={`px-4 py-3 text-xs font-semibold tracking-wider uppercase border-b-2 transition-colors whitespace-nowrap ${activeTab === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setActiveTab(t.key)} data-testid={`tab-${t.key}`}>
              {t.label} <span className={`ml-1 ${t.color}`}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Outstanding Tab */}
        {activeTab === 'outstanding' && (
          <div className="space-y-2">
            {filteredOutstanding.length === 0 ? (
              <Card><CardContent className="p-8 text-center"><CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" /><p className="font-medium">All caught up!</p><p className="text-sm text-muted-foreground">No outstanding payments</p></CardContent></Card>
            ) : filteredOutstanding.map(b => {
              const sym = getCurrencySymbol(b.currency || 'USD');
              const balance = b.balance || ((b.total_amount || 0) - (b.amount_paid || 0));
              const expanded = expandedRows.has(b.id);
              return (
                <Card key={b.id} className="border-border hover:shadow-sm transition-shadow" data-testid={`outstanding-row-${b.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{b.guest_name || 'Unknown'}</span>
                          <span className="text-xs text-muted-foreground font-mono">{b.booking_reference}</span>
                          <PaymentStatusBadge status={b.payment_status} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">{b.room_type_name} · {b.property_name} · {b.check_in} to {b.check_out}</p>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right"><p className="text-xs text-muted-foreground">Total</p><p className="font-medium">{sym}{(b.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></div>
                        <div className="text-right"><p className="text-xs text-red-600">Balance</p><p className="font-semibold text-red-700">{sym}{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p></div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => setPaymentModal(b)} data-testid={`record-payment-${b.id}`}><Plus className="h-3.5 w-3.5 mr-1" />Pay</Button>
                          <Button size="sm" variant="outline" onClick={() => setDetailModal(b.id)} data-testid={`view-ledger-${b.id}`}><Receipt className="h-3.5 w-3.5 mr-1" />History</Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleRow(b.id)}>{expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
                        </div>
                      </div>
                    </div>
                    {expanded && (
                      <div className="mt-3 pt-3 border-t grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-muted-foreground">Source:</span> <span className="font-medium">{b.source || 'direct'}</span></div>
                        <div><span className="text-muted-foreground">Nights:</span> <span className="font-medium">{b.nights}</span></div>
                        <div><span className="text-muted-foreground">Method:</span> <span className="font-medium capitalize">{b.payment_method}</span></div>
                        <div><span className="text-muted-foreground">Booked:</span> <span className="font-medium">{b.created_at ? format(new Date(b.created_at), 'd MMM yyyy') : '—'}</span></div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* All Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-2">
            {filteredPayments.length === 0 ? (
              <Card><CardContent className="p-8 text-center"><Receipt className="h-10 w-10 text-muted-foreground mx-auto mb-3" /><p className="font-medium">No payments recorded yet</p></CardContent></Card>
            ) : filteredPayments.map(p => {
              const sym = getCurrencySymbol(p.currency || 'USD');
              const acct = accounts.find(a => a.id === p.payment_account_id);
              return (
                <Card key={p.id} className="border-border" data-testid={`payment-entry-${p.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{sym}{p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          <Badge variant="outline" className="text-[10px] capitalize">{p.method}</Badge>
                          <span className="text-xs text-muted-foreground font-mono">{p.booking_reference}</span>
                          {acct && <Badge className="bg-violet-100 text-violet-700 text-[10px]">{acct.name}</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">{p.guest_name} · {p.property_name}{p.note && ` · ${p.note}`}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{p.created_at ? format(new Date(p.created_at), 'd MMM yyyy, HH:mm') : '—'}{p.recorded_by_name && ` by ${p.recorded_by_name}`}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setDetailModal(p.booking_id)} className="text-muted-foreground">View</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Receivable Tab */}
        {activeTab === 'receivable' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{receivableAccounts.length} receivable accounts · Total: {totalReceivable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <Button size="sm" onClick={() => setAccountFormModal({ account_type: 'receivable' })} data-testid="add-receivable-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add Receivable</Button>
            </div>
            {receivableAccounts.length === 0 ? (
              <Card><CardContent className="p-8 text-center"><ArrowDownLeft className="h-10 w-10 text-blue-400 mx-auto mb-3" /><p className="font-medium">No receivable accounts</p><p className="text-sm text-muted-foreground">Add travel agents or corporate customers who owe you</p></CardContent></Card>
            ) : receivableAccounts.map(renderAccountRow)}
          </div>
        )}

        {/* Payable Tab */}
        {activeTab === 'payable' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{payableAccounts.length} payable accounts · Total: {totalPayable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <Button size="sm" onClick={() => setAccountFormModal({ account_type: 'payable' })} data-testid="add-payable-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add Payable</Button>
            </div>
            {payableAccounts.length === 0 ? (
              <Card><CardContent className="p-8 text-center"><ArrowUpRight className="h-10 w-10 text-orange-400 mx-auto mb-3" /><p className="font-medium">No payable accounts</p><p className="text-sm text-muted-foreground">Add accounts for commissions or refunds you owe</p></CardContent></Card>
            ) : payableAccounts.map(renderAccountRow)}
          </div>
        )}

        {/* All Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="space-y-2">
            {accounts.length === 0 ? (
              <Card><CardContent className="p-8 text-center"><Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" /><p className="font-medium">No payment accounts yet</p><p className="text-sm text-muted-foreground">Add corporate customers, travel agents or business accounts</p></CardContent></Card>
            ) : accounts.map(renderAccountRow)}
          </div>
        )}
      </div>

      {/* Modals */}
      {paymentModal && <RecordPaymentModal booking={paymentModal} accounts={accounts} onClose={() => setPaymentModal(null)} onSuccess={handlePaymentSuccess} />}
      {detailModal && <BookingLedgerDetail bookingId={detailModal} onClose={() => setDetailModal(null)} />}
      {accountFormModal !== null && <AccountFormModal account={accountFormModal.id ? accountFormModal : null} properties={properties} onClose={() => setAccountFormModal(null)} onSuccess={handleAccountSaved} />}
      {accountLedgerModal && <AccountLedgerModal accountId={accountLedgerModal} onClose={() => setAccountLedgerModal(null)} />}
    </AdminLayout>
  );
}
